/** Designer state with undo/redo history. Domain data lives in node.data.wf / edge.data.wf. */
import { create } from 'zustand';
import { applyEdgeChanges, applyNodeChanges, type Connection, type EdgeChange, type NodeChange } from '@xyflow/react';
import { validateWorkflowDefinition, type WorkflowDefinition, type WorkflowEdge, type WorkflowNode, type WorkflowValidationIssue } from '@pqrs/contracts';
import { toDefinition, toFlow, toFlowEdge, uniqueId, type DEdge, type DNode } from './model';

interface Snapshot { nodes: DNode[]; edges: DEdge[]; slaPolicyCode?: string }
interface Clipboard { nodes: WorkflowNode[]; edges: WorkflowEdge[] }

export interface DesignerState {
  nodes: DNode[]; edges: DEdge[]; slaPolicyCode?: string;
  selectedNodeId: string | null; selectedEdgeId: string | null;
  past: Snapshot[]; future: Snapshot[]; dirty: boolean; readOnly: boolean; clipboard: Clipboard | null;
  issues: WorkflowValidationIssue[];
  load: (def: WorkflowDefinition, readOnly: boolean) => void;
  onNodesChange: (changes: NodeChange<DNode>[]) => void;
  onEdgesChange: (changes: EdgeChange<DEdge>[]) => void;
  onConnect: (c: Connection) => void;
  commit: () => void;
  updateNode: (id: string, patch: Partial<WorkflowNode> | ((n: WorkflowNode) => WorkflowNode)) => void;
  updateEdge: (id: string, patch: Partial<WorkflowEdge>) => void;
  addNode: (node: WorkflowNode, position: { x: number; y: number }) => string;
  removeNodes: (ids: string[]) => void;
  removeEdge: (id: string) => void;
  duplicateNode: (id: string) => string | null;
  copy: () => void; paste: (offset?: { x: number; y: number }) => void;
  undo: () => void; redo: () => void;
  select: (nodeId: string | null, edgeId?: string | null) => void;
  setSla: (code?: string) => void;
  markSaved: () => void;
  definition: () => WorkflowDefinition;
}

const MAX_HISTORY = 100;
const snap = (s: Pick<DesignerState, 'nodes' | 'edges' | 'slaPolicyCode'>): Snapshot => ({ nodes: s.nodes, edges: s.edges, slaPolicyCode: s.slaPolicyCode });
/** The designer only edits type workflows (the triage is a system workflow). */
const validate = (def: WorkflowDefinition) => validateWorkflowDefinition(def, { role: 'type' });
const revalidate = (s: Pick<DesignerState, 'nodes' | 'edges' | 'slaPolicyCode'>) => validate(toDefinition(s.nodes, s.edges, s.slaPolicyCode));

export const useDesigner = create<DesignerState>((set, get) => ({
  nodes: [], edges: [], slaPolicyCode: undefined, selectedNodeId: null, selectedEdgeId: null, past: [], future: [], dirty: false, readOnly: false, clipboard: null, issues: [],

  load: (def, readOnly) => { const { nodes, edges } = toFlow(def); set({ nodes, edges, slaPolicyCode: def.slaPolicyCode, past: [], future: [], dirty: false, readOnly, selectedNodeId: null, selectedEdgeId: null, issues: validate(def) }); },

  onNodesChange: (changes) => {
    const s = get();
    const nodes = applyNodeChanges(changes, s.nodes);
    const sel = changes.find((c) => c.type === 'select');
    const patch: Partial<DesignerState> = { nodes };
    if (sel) { const selected = nodes.find((n) => n.selected); patch.selectedNodeId = selected?.id ?? null; if (selected) patch.selectedEdgeId = null; }
    if (changes.some((c) => c.type === 'position' && c.dragging === false)) { patch.dirty = true; }
    set(patch);
  },
  onEdgesChange: (changes) => {
    const s = get();
    const edges = applyEdgeChanges(changes, s.edges);
    const sel = changes.find((c) => c.type === 'select');
    const patch: Partial<DesignerState> = { edges };
    if (sel) { const selected = edges.find((e) => e.selected); patch.selectedEdgeId = selected?.id ?? null; if (selected) patch.selectedNodeId = null; }
    set(patch);
  },
  onConnect: (c) => {
    const s = get();
    if (s.readOnly || !c.source || !c.target) return;
    if (s.edges.some((e) => e.source === c.source && e.target === c.target)) return;
    s.commit();
    const taken = new Set(s.edges.map((e) => e.id));
    const src = s.nodes.find((n) => n.id === c.source)?.data.wf;
    const wf: WorkflowEdge = { id: uniqueId('e', taken), from: c.source, to: c.target, isDefault: false };
    // Human task with a single outcome routes by that outcome; more outcomes -> pick the first unrouted.
    if (src?.type === 'human_task') { const used = new Set(s.edges.filter((e) => e.source === c.source).map((e) => e.data?.wf.outcome)); const free = src.outcomes.find((o) => !used.has(o)); if (free) wf.outcome = free; }
    if (src?.type === 'decision' && !s.edges.some((e) => e.source === c.source && e.data?.wf.isDefault)) wf.isDefault = true;
    const edges = [...s.edges, toFlowEdge(wf)];
    set({ edges, dirty: true, issues: revalidate({ ...s, edges }) });
  },
  commit: () => { const s = get(); set({ past: [...s.past.slice(-MAX_HISTORY + 1), snap(s)], future: [] }); },
  updateNode: (id, patch) => {
    const s = get(); if (s.readOnly) return; s.commit();
    const nodes = s.nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, wf: (typeof patch === 'function' ? patch(n.data.wf) : ({ ...n.data.wf, ...patch } as WorkflowNode)) } } : n));
    set({ nodes, dirty: true, issues: revalidate({ ...s, nodes }) });
  },
  updateEdge: (id, patch) => {
    const s = get(); if (s.readOnly) return; s.commit();
    const edges = s.edges.map((e) => (e.id === id ? { ...e, data: { ...e.data, wf: { ...e.data!.wf, ...patch } } } : e));
    set({ edges, dirty: true, issues: revalidate({ ...s, edges }) });
  },
  addNode: (node, position) => {
    const s = get(); if (s.readOnly) return node.id; s.commit();
    const taken = new Set(s.nodes.map((n) => n.id));
    const id = taken.has(node.id) ? uniqueId(node.type, taken) : node.id;
    const wf = { ...node, id, position };
    const nodes = [...s.nodes.map((n) => ({ ...n, selected: false })), { id, type: wf.type, position, data: { wf }, selected: true } as DNode];
    set({ nodes, edges: s.edges.map((e) => ({ ...e, selected: false })), selectedNodeId: id, selectedEdgeId: null, dirty: true, issues: revalidate({ ...s, nodes }) });
    return id;
  },
  removeNodes: (ids) => {
    const s = get(); if (s.readOnly || !ids.length) return; s.commit();
    const set_ = new Set(ids);
    const nodes = s.nodes.filter((n) => !set_.has(n.id));
    const edges = s.edges.filter((e) => !set_.has(e.source) && !set_.has(e.target));
    set({ nodes, edges, selectedNodeId: null, dirty: true, issues: revalidate({ ...s, nodes, edges }) });
  },
  removeEdge: (id) => {
    const s = get(); if (s.readOnly) return; s.commit();
    const edges = s.edges.filter((e) => e.id !== id);
    set({ edges, selectedEdgeId: null, dirty: true, issues: revalidate({ ...s, edges }) });
  },
  duplicateNode: (id) => {
    const s = get(); const src = s.nodes.find((n) => n.id === id); if (!src || s.readOnly || src.data.wf.type === 'start') return null;
    const copy = structuredClone(src.data.wf) as WorkflowNode; copy.name = `${copy.name} (copia)`;
    return s.addNode({ ...copy, id: `${src.data.wf.type}` }, { x: src.position.x + 40, y: src.position.y + 60 });
  },
  copy: () => {
    const s = get(); const sel = s.nodes.filter((n) => n.selected && n.data.wf.type !== 'start');
    if (!sel.length) return;
    const ids = new Set(sel.map((n) => n.id));
    set({ clipboard: { nodes: sel.map((n) => structuredClone({ ...n.data.wf, position: n.position })), edges: s.edges.filter((e) => ids.has(e.source) && ids.has(e.target)).map((e) => structuredClone(e.data!.wf)) } });
  },
  paste: (offset = { x: 60, y: 60 }) => {
    const s = get(); const cb = s.clipboard; if (!cb || s.readOnly) return; s.commit();
    const taken = new Set(s.nodes.map((n) => n.id)); const takenE = new Set(s.edges.map((e) => e.id)); const map = new Map<string, string>();
    const newNodes: DNode[] = cb.nodes.map((n) => { const id = uniqueId(n.type, taken); taken.add(id); map.set(n.id, id); const pos = { x: n.position.x + offset.x, y: n.position.y + offset.y }; return { id, type: n.type, position: pos, data: { wf: { ...structuredClone(n), id, position: pos } }, selected: true }; });
    const newEdges: DEdge[] = cb.edges.map((e) => { const id = uniqueId('e', takenE); takenE.add(id); return toFlowEdge({ ...structuredClone(e), id, from: map.get(e.from)!, to: map.get(e.to)! }); });
    const nodes = [...s.nodes.map((n) => ({ ...n, selected: false })), ...newNodes]; const edges = [...s.edges, ...newEdges];
    set({ nodes, edges, selectedNodeId: newNodes.length === 1 ? newNodes[0]!.id : null, selectedEdgeId: null, dirty: true, issues: revalidate({ ...s, nodes, edges }), clipboard: { ...cb, nodes: cb.nodes.map((n) => ({ ...n, position: { x: n.position.x + offset.x, y: n.position.y + offset.y } })) } });
  },
  undo: () => { const s = get(); const prev = s.past[s.past.length - 1]; if (!prev) return; set({ ...prev, past: s.past.slice(0, -1), future: [snap(s), ...s.future], dirty: true, issues: revalidate(prev), selectedNodeId: prev.nodes.find((n) => n.selected)?.id ?? null, selectedEdgeId: prev.edges.find((e) => e.selected)?.id ?? null }); },
  redo: () => { const s = get(); const next = s.future[0]; if (!next) return; set({ ...next, past: [...s.past, snap(s)], future: s.future.slice(1), dirty: true, issues: revalidate(next), selectedNodeId: next.nodes.find((n) => n.selected)?.id ?? null, selectedEdgeId: next.edges.find((e) => e.selected)?.id ?? null }); },
  select: (nodeId, edgeId = null) => { const s = get(); set({ selectedNodeId: nodeId, selectedEdgeId: edgeId, nodes: s.nodes.map((n) => ({ ...n, selected: n.id === nodeId })), edges: s.edges.map((e) => ({ ...e, selected: e.id === edgeId })) }); },
  setSla: (code) => { const s = get(); if (s.readOnly) return; s.commit(); set({ slaPolicyCode: code, dirty: true }); },
  markSaved: () => set({ dirty: false }),
  definition: () => { const s = get(); return toDefinition(s.nodes, s.edges, s.slaPolicyCode); },
}));
